/**
 * MÓDULO CANÓNICO DE SESIÓN (1/3) — el store del access token.
 *
 * Contrato del ecosistema: `docs-ecosistema` → modelo-de-auth, «Ciclo de vida
 * del token en las webs». Los tres ficheros (`tokenStore` / `renovacion` /
 * `sesion`) tienen el MISMO nombre y la MISMA API en todas las webs SSO; lo
 * único que cambia por repo es el ALMACENAMIENTO de aquí y el cierre de sesión
 * local de `sesion.ts`. Si se toca la API, se toca en todas y en el documento.
 *
 * Aquí el token vive en MEMORIA (no localStorage, no sessionStorage):
 * - localStorage es accesible vía JS → vulnerable a XSS.
 * - sessionStorage hereda el mismo problema.
 * - El refresh token vive como cookie httpOnly (set por el gateway,
 *   `Domain=.tecopos.com`), inaccesible desde JS. Al recargar la página el
 *   access token se pierde y la adopción de sesión lo revive.
 * `tickets-web` usa `localStorage` a propósito — misma API, distinto soporte;
 * ninguna de las dos cambia de soporte.
 *
 * Singleton porque hay un solo accessToken activo por pestaña. Los listeners
 * permiten que componentes (RequireAuth, RoleGuard) reaccionen a cambios.
 *
 * NOTA: la deduplicación de renovaciones (single-flight) YA NO vive aquí — se
 * mudó a `renovacion.ts`, que es quien conoce los reintentos. El store solo
 * guarda.
 */

export type OyenteToken = (token: string | null) => void

let accessToken: string | null = null
const oyentes = new Set<OyenteToken>()

function obtener(): string | null {
  return accessToken
}

/** `null` limpia. Sin cambio real no emite. */
function fijar(token: string | null): void {
  if (accessToken === token) return
  accessToken = token
  oyentes.forEach((oyente) => oyente(accessToken))
}

function limpiar(): void {
  fijar(null)
}

/** Suscribe a cambios del token. Devuelve la función de desuscripción. */
function suscribir(oyente: OyenteToken): () => void {
  oyentes.add(oyente)
  return () => {
    oyentes.delete(oyente)
  }
}

// Sin `this`: los métodos son funciones de módulo, así que se pueden pasar
// sueltas (`useSyncExternalStore(tokenStore.subscribe, tokenStore.get)`) sin
// perder el receptor.
export const tokenStore = {
  get: obtener,
  set: fijar,
  clear: limpiar,
  subscribe: suscribir,
}
